import { useI18n, type MessageKey } from '@ecom/i18n';
import { Button, Card, Field, Input } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import { jibexAccountFormSchema } from '../schema';
import type { JibexAccountFormValues, JibexAccountView } from '../types';

import { whenDefined } from '@/lib/optional-props';

/**
 * « Identifiants Jibex » — client id, secret, optional store id.
 *
 * THE SECRET FIELD
 *  - `type="password"`, `autoComplete="new-password"`, no spell-check, no autocapitalise: the browser must not store,
 *    suggest or show it.
 *  - There is NO « show » toggle and no copy button, by design: the secret is never displayed, even to the person who
 *    typed it.
 *  - It is never pre-filled. With a secret already saved the field is empty and says so; blank means « keep it ».
 *  - It is cleared the moment a save succeeds, and the form never keeps it anywhere else (the parent calls `onSubmit`
 *    and nothing here is written to storage, a URL or the query cache).
 *
 * A change of client id needs a new secret (a secret belongs to the identity it was issued for) — the schema says so
 * before the request is sent.
 */
export function JibexAccountForm({
  view,
  submitting,
  onSubmit,
  onDirtyChange,
}: {
  readonly view: JibexAccountView;
  readonly submitting: boolean;
  /** Resolves when the request has settled. */
  readonly onSubmit: (values: JibexAccountFormValues) => Promise<void>;
  readonly onDirtyChange: (dirty: boolean) => void;
}): ReactElement {
  const { t } = useI18n();
  const hasAccount = view.account !== null;
  const savedClientId = view.account?.clientId ?? null;

  const schema = useMemo(
    () => jibexAccountFormSchema({ hasAccount, savedClientId }),
    [hasAccount, savedClientId],
  );

  const saved: JibexAccountFormValues = useMemo(
    () => ({
      clientId: view.account?.clientId ?? '',
      secret: '',
      carrierStoreId:
        view.account?.carrierStoreId === null || view.account === null
          ? ''
          : String(view.account.carrierStoreId),
    }),
    [view.account],
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<JibexAccountFormValues>({
    resolver: zodResolver(schema),
    defaultValues: saved,
  });

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // Re-base the form on what the server now says (after a save): the secret field goes back to empty.
  useEffect(() => {
    reset(saved);
  }, [saved, reset]);

  const message = (error: { message?: string } | undefined): string | undefined =>
    error?.message === undefined ? undefined : t(error.message as MessageKey);

  async function submit(values: JibexAccountFormValues): Promise<void> {
    await onSubmit(values);
    // Whatever happened, the typed secret does not stay in the field: after a success the form is re-based on the
    // server's answer (below); after a failure the owner types it again rather than the page keeping it.
    setValue('secret', '', { shouldDirty: false });
  }

  return (
    <Card className="p-5">
      <form
        onSubmit={(event) => {
          void handleSubmit(submit)(event);
        }}
        noValidate
        autoComplete="off"
        className="flex flex-col gap-4"
        aria-label={t('merchant.delivery.account.form.title')}
      >
        <h2 className="font-display text-lg font-semibold text-ink">
          {t('merchant.delivery.account.form.title')}
        </h2>

        <Field
          label={t('merchant.delivery.account.form.clientIdLabel')}
          hint={t('merchant.delivery.account.form.clientIdHint')}
          {...whenDefined('error', message(errors.clientId))}
        >
          {(bind) => (
            <Input
              {...bind}
              {...register('clientId')}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              maxLength={200}
              disabled={submitting}
            />
          )}
        </Field>

        <Field
          label={t('merchant.delivery.account.form.secretLabel')}
          hint={
            hasAccount
              ? t('merchant.delivery.account.form.secretHintKeep')
              : t('merchant.delivery.account.form.secretHintNew')
          }
          {...whenDefined('error', message(errors.secret))}
        >
          {(bind) => (
            <Input
              {...bind}
              {...register('secret')}
              type="password"
              autoComplete="new-password"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={600}
              disabled={submitting}
              placeholder={hasAccount ? '••••••••••••' : ''}
              data-1p-ignore="true"
              data-lpignore="true"
            />
          )}
        </Field>

        {hasAccount ? (
          <p className="-mt-2 text-xs text-good">
            {t('merchant.delivery.account.form.secretStored')}
          </p>
        ) : null}

        <Field
          label={t('merchant.delivery.account.form.storeIdLabel')}
          hint={t('merchant.delivery.account.form.storeIdHint')}
          optional
          {...whenDefined('error', message(errors.carrierStoreId))}
        >
          {(bind) => (
            <Input
              {...bind}
              {...register('carrierStoreId')}
              inputMode="numeric"
              autoComplete="off"
              disabled={submitting}
            />
          )}
        </Field>

        <div>
          <Button type="submit" loading={submitting}>
            {t('merchant.delivery.account.form.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
